/**
 * Recruitment → Reports.
 *
 * Spec: .claude/specs/reports-v3-recruitment-analytics.md (Package 2).
 *
 * Same shape as PeopleReportsPage: the body is entirely ReportShell's, and this
 * page only picks which report. Adding a recruitment report is a backend query
 * plus one entry in REPORTS.
 */
import React from "react";

import ReportShell from "../components/ReportShell";
import { ReportName } from "../api";
import { useRouteTab } from "../hooks/useRouteTab";
import { TabPillBar } from "../components/TabPillBar";

type Tab = "funnel" | "time" | "source" | "interviews" | "offers" | "pipeline" | "screening";

const REPORTS: Record<Tab, { report: ReportName; title: string; subtitle: string; label: string }> = {
  funnel: {
    report: "recruitment-funnel",
    title: "Hiring Funnel",
    subtitle: "Where candidates progress, and where they are lost",
    label: "🔻 Funnel",
  },
  time: {
    report: "time-to-hire",
    title: "Time to Hire",
    subtitle: "How long it takes from application to hire",
    label: "⏱️ Time to Hire",
  },
  source: {
    report: "source-effectiveness",
    title: "Source Effectiveness",
    subtitle: "Which channels produce hires, not just applications",
    label: "📡 Sources",
  },
  interviews: {
    report: "interviews",
    title: "Interview Load & Outcomes",
    subtitle: "Volume, no-shows, pass rate and interviewer load",
    label: "🗣️ Interviews",
  },
  offers: {
    report: "offers",
    title: "Offer Outcomes",
    subtitle: "Acceptance rate, and what declined offers were worth",
    label: "📨 Offers",
  },
  pipeline: {
    report: "pipeline-health",
    title: "Pipeline Health",
    subtitle: "What is going stale, rather than what exists",
    label: "🩺 Pipeline",
  },
  screening: {
    report: "ai-screening",
    title: "AI Screening Quality",
    subtitle: "Does the AI's recommendation predict who gets hired?",
    label: "🤖 AI Screening",
  },
};

// Funnel owns the bare /recruitment-reports path.
const SEGMENTS: Record<Tab, string> = {
  funnel: "", time: "time-to-hire", source: "sources", interviews: "interviews",
  offers: "offers", pipeline: "pipeline", screening: "screening",
};

export default function RecruitmentReportsPage() {
  const [tab, setTab] = useRouteTab<Tab>("/recruitment-reports", SEGMENTS, "funnel");
  const current = REPORTS[tab];

  return (
    <ReportShell
      report={current.report}
      title={current.title}
      subtitle={current.subtitle}
      // Handed to the shell rather than rendered above it, so the page opens on
      // its title instead of on a naked row of pills.
      tabs={
        <TabPillBar
          ariaLabel="Recruitment report"
          items={(Object.keys(REPORTS) as Tab[]).map(key => ({ key, label: REPORTS[key].label }))}
          active={tab}
          onSelect={key => setTab(key as Tab)}
          style={{ marginBottom: 20 }}
        />
      }
    />
  );
}
